import { useCallback } from 'react';
import { calculateResourceGains, consumeFood } from '../systems/resourceSystem.js';
import { resolveRaid } from '../systems/combatSystem.js';
import { updateBuildingProgress } from '../systems/buildingSystem.js';
import { healRestingWorkers, applyStarvation, matureWorkers, removeDefector, addRefugees, healSickWorkers, injureWorker } from '../systems/workerSystem.js';
import { checkGameEnding } from '../systems/victorySystem.js';
import { checkSpecialEndings } from '../systems/endingSystem.js';
import { calculateMoraleChange, checkDefection } from '../systems/moraleSystem.js';
import { checkDiseaseOutbreak, applyDisease } from '../systems/diseaseSystem.js';
import { CONFIG } from '../config/constants.js';
import { BUILDINGS } from '../config/buildings.js';
import { getSpecialMechanicHandler, calculatePassiveGeneration } from '../systems/taskEffectsSystem.js';

/**
 * Hook that handles end-of-day logic
 * Orchestrates all systems in the correct order
 */
export function useEndDay({ setGame }) {
  const handleEndDay = useCallback(() => {
    setGame(g => {
      const event = g.currentEvent;

      // 1. Calculate resource gains from task rolls
      const gains = calculateResourceGains({
        rolls: g.rolls,
        pushes: g.pushes,
        buildings: g.buildings,
        event,
        bountyTask: g.bountyTask,
        day: g.day,
        tempBonuses: { farmBonus: g.farmBonus }
      });

      // 2. Update resources
      let newFood = g.food + gains.food;
      let newWood = g.wood + gains.wood;
      let newStone = g.stone + gains.stone;
      let newMetal = (g.metal || 0) + (gains.metal || 0);
      let newKnowledge = Math.min(CONFIG.KNOWLEDGE_CAP, (g.knowledge || 0) + (gains.knowledge || 0));
      let newTechPoints = (g.techPoints || 0) + (gains.techPoints || 0);

      // 3. Update building progress
      const { buildings: newBuildings, buildingInProgress: newBuildingInProgress } = updateBuildingProgress(
        g.buildings,
        g.buildingInProgress,
        gains.buildProgress
      );

      // 4. Handle task-specific end-of-day effects using handler registry
      let newWorkers = g.workers;

      const endDayHandlers = {
        healRestingWorkers: (workers) =>
          healRestingWorkers(workers, g.assignments),

        healSickWorkers: (workers) =>
          healSickWorkers(workers, g.assignments, g.rolls),

        handleMineInjuries: (workers, task) => {
          let updatedWorkers = workers;
          const rollData = g.rolls[task];
          if (rollData?.caveIns?.length > 0) {
            rollData.caveIns.forEach(id => {
              updatedWorkers = injureWorker(updatedWorkers, id, true);
            });
          }
          return updatedWorkers;
        }
      };

      const processedTasks = new Set();

      Object.entries(g.rolls).forEach(([task, rollData]) => {
        const handlerName = getSpecialMechanicHandler(task, 'onEndDay');
        const handler = endDayHandlers[handlerName];
        if (handler) {
          newWorkers = handler(newWorkers, task);
        }
        processedTasks.add(task);
      });

      Object.entries(g.assignments).forEach(([workerId, task]) => {
        if (!processedTasks.has(task)) {
          const handlerName = getSpecialMechanicHandler(task, 'onEndDay');
          const handler = endDayHandlers[handlerName];
          if (handler) {
            newWorkers = handler(newWorkers, task);
            processedTasks.add(task);
          }
        }
      });

      // 4c. Mature children
      newWorkers = matureWorkers(newWorkers, g.day + 1);

      // 4d. Process scheduled refugee arrivals
      const nextDay = g.day + 1;
      let refugeeNotice = null;
      let newMorale = g.morale || CONFIG.STARTING_MORALE;

      if (g.futureRefugees && g.futureRefugees.day === nextDay) {
        newWorkers = addRefugees(newWorkers, g.futureRefugees.count, nextDay);
        newMorale = Math.min(100, newMorale + (g.futureRefugees.count * 3));
        refugeeNotice = {
          type: 'refugee_arrival',
          name: 'Refugees Arrived',
          icon: '🚶',
          message: `${g.futureRefugees.count} refugees arrived and joined your settlement!`
        };
      }

      // 5. Resolve combat if raid is scheduled for this day
      // Take death snapshot BEFORE combat so raid deaths register in deathsThisTurn
      const previousDeathCount = newWorkers.filter(w => w.status === 'dead').length;

      let combatResult = null;
      let newRaidsDefeated = g.raidsDefeated || 0;
      if (g.raidScheduledForDay === g.day) {
        const result = resolveRaid({
          day: g.day,
          rolls: g.rolls,
          pushes: g.pushes,
          buildings: newBuildings,
          resources: { food: newFood, wood: newWood, stone: newStone, metal: newMetal },
          workers: newWorkers,
          event,
          bountyTask: g.bountyTask
        });

        combatResult = result;
        newFood = result.resources.food;
        newWood = result.resources.wood;
        newStone = result.resources.stone;
        newMetal = result.resources.metal || newMetal;
        newWorkers = result.workers;

        if (result.won) {
          newRaidsDefeated += 1;
        }
      }

      // 6. Consume food
      const aliveWorkers = newWorkers.filter(w => w.status !== 'dead');
      const { newFood: foodAfterConsumption, starvationCount } = consumeFood(
        newFood,
        aliveWorkers.length
      );
      newFood = foodAfterConsumption;

      // 6b. Nightly food spoilage (5%; 2% with granary)
      const hasGranary = newBuildings.some(b => b.type === 'granary' && b.complete);
      const spoilageRate = hasGranary ? 0.02 : 0.05;
      const spoiled = Math.floor(newFood * spoilageRate);
      newFood = Math.max(0, newFood - spoiled);

      // 7. Apply starvation
      if (starvationCount > 0) {
        newWorkers = applyStarvation(newWorkers, starvationCount);
      }
      const newDeathCount = newWorkers.filter(w => w.status === 'dead').length;
      const deathsThisTurn = newDeathCount - previousDeathCount;

      // 7a. Update morale based on events
      // Find newly completed buildings and apply their completion morale
      const newlyCompleted = newBuildings.filter(b =>
        b.complete && !g.buildings.find(gb => gb.type === b.type && gb.complete)
      );
      let buildingCompletionMorale = 0;
      newlyCompleted.forEach(building => {
        const buildingDef = BUILDINGS[building.type];
        buildingCompletionMorale += buildingDef?.completionEffects?.morale ?? 5;
      });

      newMorale = calculateMoraleChange(newMorale, {
        combatWon: combatResult?.won,
        combatLost: combatResult && !combatResult.won,
        deaths: deathsThisTurn,
        starvation: starvationCount > 0
        // Building completion handled separately below
      });

      if (buildingCompletionMorale > 0) {
        newMorale = Math.min(100, newMorale + buildingCompletionMorale);
      }

      // 7a-drift. Morale drifts toward 50 by 1/day
      newMorale = newMorale > 50 ? newMorale - 1 : newMorale < 50 ? newMorale + 1 : newMorale;
      newMorale = Math.max(0, Math.min(100, newMorale));

      // 7b. Check for defection
      const defector = checkDefection(newMorale, newWorkers);
      if (defector) {
        newWorkers = removeDefector(newWorkers, defector.id);
        newMorale = Math.max(0, newMorale - 15);
      }

      // 7c. Generate passive resources from buildings
      const passiveGains = calculatePassiveGeneration(newBuildings, {
        knowledge: newKnowledge,
        techPoints: newTechPoints
      });

      newKnowledge += passiveGains.knowledge || 0;
      newTechPoints += passiveGains.techPoints || 0;

      // 7d. Check for disease outbreak
      let diseaseNotice = null;
      const diseaseEvent = checkDiseaseOutbreak({
        ...g,
        workers: newWorkers,
        buildings: newBuildings,
        morale: newMorale
      });

      if (diseaseEvent) {
        newWorkers = applyDisease(newWorkers, diseaseEvent.victims);
        diseaseNotice = diseaseEvent;
      }

      // 7e. Update death log
      const newDeathLog = [...(g.deathLog || [])];
      if (deathsThisTurn > 0) {
        newDeathLog.push({ day: g.day, count: deathsThisTurn });
      }

      // 8. Check victory/defeat with ending system
      const fullUpdatedState = {
        ...g,
        day: nextDay,
        workers: newWorkers,
        buildings: newBuildings,
        food: Math.max(0, newFood),
        wood: newWood,
        stone: newStone,
        metal: newMetal,
        knowledge: newKnowledge,
        morale: newMorale,
        techPoints: newTechPoints,
        combatResult,
        deathLog: newDeathLog
      };

      const endingCheck = checkGameEnding(fullUpdatedState);
      const gameOver = endingCheck?.type === 'defeat';
      const gameWon = endingCheck && endingCheck.type !== 'defeat';

      // 8b. Daily special-ending check for claim prompt (only before WIN_DAY)
      let newPendingVictory = g.pendingVictory || null;
      if (!gameOver && !gameWon && nextDay < CONFIG.WIN_DAY) {
        const specialEnding = checkSpecialEndings(fullUpdatedState);
        if (specialEnding && (!g.pendingVictory || g.pendingVictory.type !== specialEnding.type)) {
          newPendingVictory = specialEnding;
        } else if (!specialEnding) {
          newPendingVictory = null; // Conditions no longer met — clear it
        }
      }

      // 9. Determine next phase
      const nextPhase = combatResult ? 'combat'
        : (gameOver || gameWon) ? 'gameover'
        : 'event';

      // 10. Return new state
      return {
        ...g,
        phase: nextPhase,
        day: (gameOver || gameWon) ? g.day : nextDay,
        food: Math.max(0, newFood),
        wood: newWood,
        stone: newStone,
        metal: newMetal,
        knowledge: newKnowledge,
        morale: newMorale,
        techPoints: newTechPoints,
        workers: newWorkers,
        buildings: newBuildings,
        buildingInProgress: newBuildingInProgress,
        combatResult,
        raidScheduledForDay: g.raidScheduledForDay === g.day ? null : g.raidScheduledForDay,
        raidWarning: false,
        currentEvent: (gameOver || gameWon) ? g.currentEvent : null,
        eventRoll: null,
        lastAssignments: g.assignments,
        assignments: {},
        rolls: {},
        pushes: {},
        tempWorker: null,
        bountyTask: null,
        gameOver,
        gameWon,
        ending: endingCheck,
        deathLog: newDeathLog,
        raidsDefeated: newRaidsDefeated,
        futureRefugees: (g.futureRefugees && g.futureRefugees.day === nextDay) ? null : g.futureRefugees,
        futureRefugeesArrivalNotice: refugeeNotice,
        diseaseNotice,
        pendingVictory: newPendingVictory
      };
    });
  }, [setGame]);

  return handleEndDay;
}
